import type { RubroRow } from './projectAutofillDraftUtils.ts'
import {
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots
} from './projectAutofillDraftUtils.ts'
import type { DocumentationSlotKey } from './documentationSlotSummaryUtils.ts'

export function clearRubroSlotAssignment(
  rubro: RubroRow,
  slot: DocumentationSlotKey
): Partial<Omit<RubroRow, 'tempId'>> {
  const slots = normalizeDocumentationSlots(rubro.documentationSlots ?? {})
  const fileIds = normalizeDocumentationFileIds(rubro.documentationFileIds ?? {})
  const nextSlots = { ...slots, [slot]: [] as number[] }
  const nextFileIds = { ...fileIds }
  delete nextFileIds[slot]

  const patch: Partial<Omit<RubroRow, 'tempId'>> = {
    documentationSlots: nextSlots,
    documentationFileIds: Object.keys(nextFileIds).length ? nextFileIds : undefined
  }

  if (slot === 'technicalSpecifications') {
    patch.technicalSpecificationState = undefined
  }
  if (slot === 'metricComputation') {
    patch.metricSheetState = undefined
  }
  if (slot === 'budget') {
    patch.budgetSheetState = undefined
  }

  return patch
}

function cloneSnapshotValue<T>(value: T | undefined): T | undefined {
  if (value === undefined) return undefined
  return JSON.parse(JSON.stringify(value)) as T
}

export function captureRubroSlotAssignment(
  rubro: RubroRow,
  slot: DocumentationSlotKey
): Partial<Omit<RubroRow, 'tempId'>> | null {
  const slots = normalizeDocumentationSlots(rubro.documentationSlots ?? {})
  const fileIds = normalizeDocumentationFileIds(rubro.documentationFileIds ?? {})
  const pages = slots[slot] ?? []
  const fid = String(fileIds[slot] ?? '').trim()
  const hadPages = pages.length > 0
  const hadFile = !!fid
  const hadTs = slot === 'technicalSpecifications' && rubro.technicalSpecificationState != null
  const hadMs = slot === 'metricComputation' && rubro.metricSheetState != null
  const hadBs = slot === 'budget' && rubro.budgetSheetState != null

  if (!hadPages && !hadFile && !hadTs && !hadMs && !hadBs) return null

  const patch: Partial<Omit<RubroRow, 'tempId'>> = {
    documentationSlots: cloneSnapshotValue(slots),
    documentationFileIds: cloneSnapshotValue(fileIds)
  }

  if (hadTs) patch.technicalSpecificationState = cloneSnapshotValue(rubro.technicalSpecificationState)
  if (hadMs) patch.metricSheetState = cloneSnapshotValue(rubro.metricSheetState)
  if (hadBs) patch.budgetSheetState = cloneSnapshotValue(rubro.budgetSheetState)

  const slotSrc = rubro.documentationSlotSources?.[slot]
  if (slotSrc != null) {
    patch.documentationSlotSources = {
      ...(rubro.documentationSlotSources ?? {}),
      [slot]: cloneSnapshotValue(slotSrc)
    }
  }

  return patch
}

export function restoreRubroSlotAssignment(
  rubro: RubroRow,
  snapshot: Partial<Omit<RubroRow, 'tempId'>>
): Partial<Omit<RubroRow, 'tempId'>> {
  const slots = normalizeDocumentationSlots(rubro.documentationSlots ?? {})
  const fileIds = normalizeDocumentationFileIds(rubro.documentationFileIds ?? {})
  const snapSlots = snapshot.documentationSlots
  const snapFileIds = snapshot.documentationFileIds

  const patch: Partial<Omit<RubroRow, 'tempId'>> = {
    documentationSlots: snapSlots ? { ...slots, ...snapSlots } : slots,
    documentationFileIds: snapFileIds ? { ...fileIds, ...snapFileIds } : fileIds
  }

  if ('technicalSpecificationState' in snapshot) {
    patch.technicalSpecificationState = snapshot.technicalSpecificationState
  }
  if ('metricSheetState' in snapshot) {
    patch.metricSheetState = snapshot.metricSheetState
  }
  if ('budgetSheetState' in snapshot) {
    patch.budgetSheetState = snapshot.budgetSheetState
  }
  if (snapshot.documentationSlotSources) {
    patch.documentationSlotSources = {
      ...(rubro.documentationSlotSources ?? {}),
      ...snapshot.documentationSlotSources
    }
  }

  return patch
}
